import pino from "pino";

const isDev = process.env.NODE_ENV === "development";
const isEdge = process.env.NEXT_RUNTIME === "edge";
const configuredLevel = process.env.LOG_LEVEL?.trim();
const level = configuredLevel || (isDev ? "debug" : "info");

/** `pino.transport` is Node-only; Next/Webpack client bundles omit it and crash if called. */
const devPrettyDestination =
  isDev && !isEdge && typeof pino.transport === "function"
    ? pino.transport({
        target: "pino-pretty",
        options: {
          colorize: true,
          translateTime: "SYS:standard",
          ignore: "pid,hostname",
        },
      })
    : undefined;

const baseLogger = isEdge ? pino({ level }) : pino({ level }, devPrettyDestination);

export interface ApiLogContext {
  route: string;
  method: string;
  userId?: string;
  durationMs?: number;
  status?: number;
}

export interface PillCountLogContext {
  userId: string;
  scanId?: number;
  pillCount?: number;
  confidence?: number;
  processingMs?: number;
  cached?: boolean;
}

export interface SearchLogContext {
  userId: string;
  query: string;
  resultCount: number;
  durationMs?: number;
}

export interface BillingLogContext {
  userId: string;
  event: string;
  plan?: string;
  stripeEventId?: string;
}

export interface ErrorLogContext {
  route?: string;
  userId?: string;
  errorCode?: string;
  [key: string]: unknown;
}

export const apiLogger = baseLogger.child({ module: "api" });
export const pillCountLogger = baseLogger.child({ module: "pill-counter" });
export const searchLogger = baseLogger.child({ module: "search" });
export const billingLogger = baseLogger.child({ module: "billing" });
export const authLogger = baseLogger.child({ module: "auth" });
export const cronLogger = baseLogger.child({ module: "cron" });
export const dbLogger = baseLogger.child({ module: "db" });

export function logApiRequest(ctx: ApiLogContext) {
  apiLogger.info(ctx, `${ctx.method} ${ctx.route}`);
}

export function logApiError(ctx: ErrorLogContext, error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  apiLogger.error({ ...ctx, error: message }, `Error in ${ctx.route ?? "unknown route"}`);
}

export function logPillCount(ctx: PillCountLogContext) {
  pillCountLogger.info(ctx, `Pill count: ${ctx.pillCount} pills (confidence: ${ctx.confidence})`);
}

export function logSearch(ctx: SearchLogContext) {
  if (ctx.resultCount === 0) searchLogger.warn(ctx, `Zero results for query: "${ctx.query}"`);
  else searchLogger.debug(ctx, `Search: "${ctx.query}" -> ${ctx.resultCount} results`);
}

export function logBillingEvent(ctx: BillingLogContext) {
  billingLogger.info(ctx, `Billing: ${ctx.event}`);
}

const logger = baseLogger.child({ module: "app" });
export default logger;
